// List of all available namespaces after splitting translations.
export const namespaces = [
  "about",
  "analytics",
  "business",
  "calculator",
  "careers",
  "common",
  "contact",
  "faq",
  "features",
  "home",
  "onboarding",
  "press",
  "privacy",
  "resources",
  "services",
  "solutions",
  "terms",
  "navigation",
  "footer",
  "layout",
  "vadArCaire",
  "articles/comprehensive-ai-in-home-care",
  "articles/future-homecare-trends",
  "articles/balancing-efficiency-continuity",
  "articles/kontinuitet-effektivitet-ai",
  "articles/optimization-continuity-algorithms",
  "articles/vrptw-home-care-optimization",
  "articles/constraint-management-homecare-scheduling",
  "articles/efficiency-80-hemtjanst",
  "articles/mission-efter-ms",
  "articles/en-dag-i-hemtjansten-2026",
  "articles/schemaoptimering-brukartid-restid",
  "auth",
  "effective-staff-planning",
  "faq",
  "guides/comprehensive-ai-implementation",
  "optimize-home-care-schedule",
  "scheduling-comparison",
  "seo",
  "saFungerarCaire",
  "plattform",
];

const routeNamespaceRules: ReadonlyArray<readonly [RegExp, string]> = [
  [/\/(?:sa-fungerar-caire|en\/how-caire-works)/, "saFungerarCaire"],
  // Anchored: unanchored, it would also claim any path merely containing it.
  [/^\/(?:plattform|en\/platform)\/?$/, "plattform"],
  [
    /\/(?:resurser\/artiklar\/framtidens-hemtjanst-trender|en\/resources\/articles\/future-home-care-trends)/,
    "articles/future-homecare-trends",
  ],
  [
    /\/(?:resurser\/artiklar\/omfattande-guide-ai-i-hemtjansten|en\/resources\/articles\/comprehensive-ai-in-home-care)/,
    "articles/comprehensive-ai-in-home-care",
  ],
  [
    /\/(?:resurser\/artiklar\/optimering-kontinuitet-algoritmer|en\/resources\/articles\/optimization-continuity-algorithms)/,
    "articles/optimization-continuity-algorithms",
  ],
  [
    /\/(?:resurser\/artiklar\/vrptw-hemtjanst-optimering|en\/resources\/articles\/vrptw-home-care-optimization)/,
    "articles/vrptw-home-care-optimization",
  ],
  [
    /\/(?:resurser\/artiklar\/balansera-effektivitet-kontinuitet|en\/resources\/articles\/balancing-efficiency-continuity)/,
    "articles/balancing-efficiency-continuity",
  ],
  [
    /\/(?:resurser\/artiklar\/kontinuitet-effektivitet-ai|en\/resources\/articles\/continuity-efficiency-ai)/,
    "articles/kontinuitet-effektivitet-ai",
  ],
  [
    /\/(?:resurser\/artiklar\/villkorshantering-hemtjanst-schemaoptimering|en\/resources\/articles\/constraint-management-homecare-scheduling)/,
    "articles/constraint-management-homecare-scheduling",
  ],
  [
    /\/(?:resurser\/artiklar\/80-procent-brukartid-hemtjanst|en\/resources\/articles\/80-percent-efficiency-home-care)/,
    "articles/efficiency-80-hemtjanst",
  ],
  [
    /\/(?:resurser\/artiklar\/schemaoptimering-brukartid-restid|en\/resources\/articles\/schedule-optimization-care-time-travel-time)/,
    "articles/schemaoptimering-brukartid-restid",
  ],
  [
    /\/(?:resurser\/artiklar\/mission-efter-ms|en\/resources\/articles\/mission-beyond-limits)/,
    "articles/mission-efter-ms",
  ],
  [
    /\/(?:resurser\/artiklar\/en-dag-i-hemtjansten-2026|en\/resources\/articles\/a-day-in-home-care-2026)/,
    "articles/en-dag-i-hemtjansten-2026",
  ],
  [
    /\/(?:resurser\/guider\/effektiv-personalplanering-hemtjanst|en\/resources\/guides\/effective-staff-planning-home-care)/,
    "effective-staff-planning",
  ],
  [
    /\/(?:resurser\/guider\/optimera-hemtjanst-schema|en\/resources\/guides\/optimize-home-care-schedule)/,
    "optimize-home-care-schedule",
  ],
  [
    /\/(?:resurser\/jamforelser\/jamforelse-schemalaggningssystem-hemtjanst|en\/resources\/comparisons\/home-care-scheduling-tools-comparison)/,
    "scheduling-comparison",
  ],
];

/**
 * Loads shared, page-support, and route-specific translation bundles during
 * SSR so crawlers receive the same localized metadata and content as clients.
 */
export const getSsrNamespacesForRoute = (pathname: string): string[] => {
  const pageNamespace = routeNamespaceRules.find(([pattern]) =>
    pattern.test(pathname),
  )?.[1];

  return pageNamespace
    ? [
        ...new Set([
          ...criticalNamespaces,
          ...ssrSupportNamespaces,
          pageNamespace,
        ]),
      ]
    : [...criticalNamespaces, ...ssrSupportNamespaces];
};

// These namespaces are critical and should be loaded immediately.
export const criticalNamespaces = [
  "common", // Contains shared translations
  "navigation", // Navigation elements
  "footer", // Footer elements
  "layout", // Layout elements
  "home", // Home page
  "about", // About page
  "resources", // Resources section
  "features", // Features section
  "services", // Services section
  "solutions", // Sitemap-visible solution pages
  "calculator", // Calculator component
  "contact", // Contact page
  "careers", // Careers page
  "vadArCaire", // What is CAIRE page
  "faq", // FAQ page — SSR has no lazy-load backend, so this must be eager
];

// These namespaces are consumed by sitemap-visible pages but not always by a
// one-to-one route component. Supplying them to every SSR request avoids an
// English render falling back to Swedish source strings before the client can
// lazy-load the required bundle.
const ssrSupportNamespaces = [
  "analytics",
  "business",
  "faq",
  "guides/comprehensive-ai-implementation",
  "onboarding",
  "press",
  "privacy",
  "terms",
];

// Helper to determine the namespace to use based on the path.
export const getNamespaceForRoute = (pathname: string): string => {
  const routeNamespace = routeNamespaceRules.find(([pattern]) =>
    pattern.test(pathname),
  )?.[1];
  if (routeNamespace) return routeNamespace;

  if (pathname.includes("/om-oss/press") || pathname.includes("/about/press")) {
    return "press";
  }
  if (pathname.includes("/om-oss") || pathname.includes("/about")) {
    return "about";
  }
  if (
    pathname.includes("/vad-ar-caire") ||
    pathname.includes("/what-is-caire")
  ) {
    return "vadArCaire";
  }
  if (pathname.includes("/karriar") || pathname.includes("/careers")) {
    return "careers";
  }
  if (pathname.includes("/kalkylator") || pathname.includes("/calculator")) {
    return "calculator";
  }
  if (pathname.includes("/kontakt") || pathname.includes("/contact")) {
    return "contact";
  }
  if (pathname.includes("/resurser") || pathname.includes("/resources")) {
    return "resources";
  }
  return "home";
};
